import axios from 'axios';
import { addOrEditItem, deleteItem, getItems, API_ROOT, logApiError } from '@/store/api';
import { clientIdFromSearch, withOwnerScope } from '@/services/onboarding_resolve';

// Wrap an onboarding write action so it targets the route's ?client_id owner when one is
// present and the caller didn't set user_id explicitly. This centrally scopes every
// intake/party mutation to the client for an admin/advisor editing a client's trust,
// instead of defaulting to the viewer's own API scope and 404-ing (ENG-2726). Self-view
// has no ?client_id, so writes stay scoped to the logged-in user.
const ownerScoped =
    action =>
    (context, payload = {}) => {
        const search = typeof window !== 'undefined' ? window.location.search : '';
        return action(context, withOwnerScope(payload, clientIdFromSearch(search)));
    };

export default {
    namespaced: true,
    state: {
        onboardingInfo: {},
        onboardings: [],
        preSelectedSolarProject: null,
        savedSolarProjects: [],
        yourSolarProjects: [],
    },
    mutations: {
        updateOnboardings(state, onboardings) {
            // Slim rows (a fetch with exclude_file_fields — the dashboard's) omit the
            // document keys entirely, unlike a full row with no documents ([] present).
            // Merge those keys forward from the previous row so a late-landing slim
            // response can never erase documents a document-consuming page (ProjectDetail,
            // DocumentReview) has already loaded into the shared list. Keep the key list
            // in sync with the backend serializer's SLOW_FIELDS.
            const previousByUuid = new Map((state.onboardings || []).map(o => [o.uuid, o]));
            state.onboardings = (onboardings || []).map(row => {
                const previous = previousByUuid.get(row.uuid);
                if (!previous) return row;
                const merged = { ...row };
                ['documents', 'generated_documents'].forEach(key => {
                    if (!(key in row) && key in previous) {
                        merged[key] = previous[key];
                    }
                });
                return merged;
            });
        },
        updatePreSelectedSolarProject(state, preSelectedSolarProject) {
            state.preSelectedSolarProject = preSelectedSolarProject;
        },
        updateSavedSolarProjects(state, savedSolarProjects) {
            state.savedSolarProjects = savedSolarProjects;
        },
        updateYourSolarProjects(state, yourSolarProjects) {
            state.yourSolarProjects = yourSolarProjects;
        },
    },
    getters: {
        onboardings: state => {
            return state.onboardings || [];
        },
        pre_selected_solar_project: state => {
            return state.preSelectedSolarProject || null;
        },
        saved_solar_projects: state => {
            return state.savedSolarProjects || [];
        },
        your_solar_projects: state => {
            return state.yourSolarProjects || [];
        },
    },
    actions: {
        // getOnboardings is ownerScoped too: several product wizards override
        // refreshOnboardings and call getOnboardings without a user_id, so scoping here
        // (from the route's ?client_id) keeps cross-user reads on the client's list
        // rather than reverting Vuex to the viewer's intakes mid-wizard (ENG-2726).
        getOnboardings: ownerScoped(getItems('onboardings/')),
        // The person address book (ENG-2783): real records, not names harvested out
        // of the legacy intake columns. A person named on signed documents forks on
        // edit instead of being overwritten, so an executed agreement keeps saying
        // what it said. ownerScoped for the same ENG-2726 reason as the actions above.
        getPersons: ownerScoped(getItems('persons/')),
        createPerson: ownerScoped(addOrEditItem('persons/')),
        updatePerson: ownerScoped(addOrEditItem('persons/')),
        deletePerson: ownerScoped(deleteItem('persons/')),
        deleteOnboarding: ownerScoped(deleteItem('onboardings/')),
        // Writes are ownerScoped so an admin/advisor editing a client's trust (route
        // carries ?client_id) targets the client's records, not the viewer's (ENG-2726).
        createOnboarding: ownerScoped(addOrEditItem('onboardings/')),
        updateOnboarding: ownerScoped(addOrEditItem('onboardings/')), // Similar to line above, naming simplification
        createOnboardingAdditionalDataItem: ownerScoped(addOrEditItem('additional-data/', 'onboardings/')),
        createOnboardingDocumentItem: ownerScoped(addOrEditItem('document/', 'onboardings/')),
        deleteOnboardingDocumentItem: ownerScoped(deleteItem('document/', 'onboardings/', true)),
        createOnboardingGeneratedDocumentItem: ownerScoped(addOrEditItem('generated-document/', 'onboardings/')),
        deleteOnboardingGeneratedDocumentItem: ownerScoped(deleteItem('generated-document/', 'onboardings/')),
        createOnboardingProfileDataItem: ownerScoped(addOrEditItem('profile-data/', 'onboardings/')),
        createOnboardingRelationshipsDataItem: ownerScoped(addOrEditItem('relationships-data/', 'onboardings/')),
        createOnboardingRelationshipsTrustDataItem: ownerScoped(
            addOrEditItem('relationships-trust-data/', 'onboardings/'),
        ),
        getOnboardingTrustAgreements: ownerScoped(getItems('generated-trust-agreements/', 'onboardings/')),
        // Field-level change log for one project. Its own request because the trail is
        // expensive to build, so nothing that doesn't display it pays for it.
        getOnboardingHistory: ownerScoped(getItems('history/', 'onboardings/')),
        // templates related actions
        getTemplate: getItems('template/', 'onboardings/'),
        saveDraftTemplate: addOrEditItem('template/save_draft/', 'onboardings/'),
        previewDraftTemplate: addOrEditItem('template/preview_draft/', 'onboardings/'),
        commitDraftTemplate: addOrEditItem('template/commit_draft/', 'onboardings/'),
        discardDraftTemplate: deleteItem('template/discard_draft/', 'onboardings/'),

        async postUserOnboardingEmail({ rootGetters }, payload) {
            await axios.post(
                `${API_ROOT}/${payload.user_id}/onboardings/${payload.onboarding_id}/send_trust_agreement/`,
                payload,
                rootGetters.axiosConfig,
            );
        },
        async approveSwap({ dispatch, rootGetters }, payload) {
            try {
                const response = await axios.post(
                    `${API_ROOT}/${payload.user_id}/onboardings/${payload.intake_id}/approve-swap/`,
                    { create_new_grat: payload.create_new_grat },
                    rootGetters.axiosConfig,
                );
                return response.data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async dismissTrigger({ dispatch, rootGetters }, payload) {
            try {
                await axios.post(
                    `${API_ROOT}/${payload.user_id}/onboardings/${payload.intake_id}/dismiss-trigger/`,
                    {},
                    rootGetters.axiosConfig,
                );
                return true;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async approveRecurringDraft({ dispatch, rootGetters }, payload) {
            try {
                const response = await axios.post(
                    `${API_ROOT}/${payload.user_id}/onboardings/${payload.onboarding_id}/recurring/approve/`,
                    {},
                    rootGetters.axiosConfig,
                );
                return response.data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async skipRecurringDraft({ dispatch, rootGetters }, payload) {
            try {
                const response = await axios.post(
                    `${API_ROOT}/${payload.user_id}/onboardings/${payload.onboarding_id}/recurring/skip/`,
                    {},
                    rootGetters.axiosConfig,
                );
                return response.data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async createMultiGratGroup({ dispatch, rootGetters }, payload) {
            try {
                const response = await axios.post(
                    `${API_ROOT}/${payload.user_id}/onboardings/${payload.onboarding_id}/multi-grat/create/`,
                    { count: payload.count },
                    rootGetters.axiosConfig,
                );
                return response.data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async cancelRecurringDraft({ dispatch, rootGetters }, payload) {
            try {
                const response = await axios.post(
                    `${API_ROOT}/${payload.user_id}/onboardings/${payload.onboarding_id}/recurring/cancel/`,
                    {},
                    rootGetters.axiosConfig,
                );
                return response.data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
        async updateRecurringSettings({ dispatch, rootGetters }, payload) {
            try {
                const response = await axios.patch(
                    `${API_ROOT}/${payload.user_id}/onboardings/${payload.onboarding_id}/recurring/`,
                    payload.data,
                    rootGetters.axiosConfig,
                );
                return response.data;
            } catch (e) {
                logApiError(e, dispatch);
                return false;
            }
        },
    },
};
